import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../contexts/auth";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getAuditStat } from "../../../api/administration";
import AccessDenied from "../../../components/access-denied/access-denied";
import LoadPanel from "devextreme-react/load-panel";
import DataGrid, {
  Column,
  Pager,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Grouping,
  GroupPanel,
  Scrolling,
  Export,
  Selection,
  Sorting,
  RemoteOperations,
} from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import CustomStore from "devextreme/data/custom_store";
import '../audit.scss'

/** Операторы DevExtreme: равенство часто приходит как "eq", не только "=". */
const FILTER_OPERATORS = [
  "=",
  "eq",
  "<>",
  "ne",
  ">",
  ">=",
  "<",
  "<=",
  "between",
  "contains",
  "startswith",
  "endswith",
  "notcontains",
];

/** Поля грида, которые уходят в фильтры API get-audit (без dat — он только на фронте). */
const FILTERABLE_FIELDS = ["email", "user_id", "fio", "entry_count", "dogovor"];

/**
 * dataField грида → sortBy / имя поля фильтра для API.
 * Ответ бэка отдаёт user_name/ndog/duration, но контракт getAuditStat
 * ждёт email/dogovor/entry_count (и userId/entryCount в options).
 */
const GRID_FIELD_TO_API = {
  dat: "dat",
  user_id: "user_id",
  fio: "fio",
  email: "email",
  entry_count: "entry_count",
  dogovor: "dogovor",
};

const DATE_FETCH_PAGE_SIZE = 500;

const DATE_COLUMN_FILTER_OPERATIONS = [
  "=",
  "<>",
  ">",
  ">=",
  "<",
  "<=",
  "between",
];

function getApiField(gridField) {
  return GRID_FIELD_TO_API[gridField] ?? gridField ?? "dat";
}

/** Фильтр по dat считаем активным только при валидной дате (не пустой редактор FilterRow). */
function isDateFilterValueActive(value, operator) {
  if (operator === "between") {
    const range = Array.isArray(value) ? value : [];
    return range.some((item) => toDate(item) !== null);
  }
  return toDate(value) !== null;
}

function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null || value === "") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Обход дерева фильтров DevExtreme.
 * onCondition(field, operator, value) вызывается для каждого [field, op, value].
 */
function walkDevExtremeFilter(rawFilter, onCondition) {
  if (!rawFilter || !Array.isArray(rawFilter)) return;

  const walk = (node) => {
    if (!Array.isArray(node)) return;

    if (node.length === 3 && FILTER_OPERATORS.includes(node[1])) {
      onCondition(node[0], node[1], node[2]);
      return;
    }

    if (node.length >= 2 && (node[0] === "and" || node[0] === "or")) {
      node.slice(1).forEach(walk);
      return;
    }

    node.forEach(walk);
  };

  walk(rawFilter);
}

/**
 * Разбор loadOptions.filter → поля для API (без dat).
 */
function parseAuditStatFilters(rawFilter) {
  const out = {};
  walkDevExtremeFilter(rawFilter, (field, _operator, value) => {
    const apiField = getApiField(field);
    if (
      FILTERABLE_FIELDS.includes(apiField) &&
      value !== undefined &&
      value !== null
    ) {
      out[apiField] = value;
    }
  });
  return out;
}

/**
 * Есть ли в дереве фильтров условие по колонке dat.
 */
function hasDateColumnFilter(rawFilter) {
  let found = false;
  walkDevExtremeFilter(rawFilter, (field, operator, value) => {
    if (
      getApiField(field) === "dat" &&
      isDateFilterValueActive(value, operator)
    ) {
      found = true;
    }
  });
  return found;
}

function buildPeriodCacheKey(periodStart, periodEnd, queryBase) {
  return JSON.stringify({
    start: periodStart.getTime(),
    end: periodEnd.getTime(),
    ...queryBase,
  });
}

function calculateDatFilterExpression(filterValue, selectedFilterOperation) {
  if (!isDateFilterValueActive(filterValue, selectedFilterOperation)) {
    return null;
  }

  if (selectedFilterOperation === "between") {
    return ["dat", "between", filterValue];
  }

  return ["dat", selectedFilterOperation, toDate(filterValue)];
}

/**
 * Проверка строки по одному условию фильтра колонки dat.
 * Для = / <> сравниваем по календарному дню (в колонке datetime со временем).
 */
function rowMatchesDateCondition(rowDat, operator, value) {
  const rowDate = toDate(rowDat);
  if (!rowDate) return false;
  const rowTime = rowDate.getTime();

  if (operator === "between") {
    const [fromRaw, toRaw] = Array.isArray(value) ? value : [];
    const from = toDate(fromRaw);
    const to = toDate(toRaw);
    if (!from || !to) return false;
    return (
      rowTime >= startOfDay(from).getTime() &&
      rowTime <= endOfDay(to).getTime()
    );
  }

  const filterDate = toDate(value);
  if (!filterDate) return false;

  switch (operator) {
    case "=":
    case "eq":
      return (
        rowTime >= startOfDay(filterDate).getTime() &&
        rowTime <= endOfDay(filterDate).getTime()
      );
    case "<>":
    case "ne":
      return (
        rowTime < startOfDay(filterDate).getTime() ||
        rowTime > endOfDay(filterDate).getTime()
      );
    case ">":
      return rowTime > filterDate.getTime();
    case ">=":
      return rowTime >= startOfDay(filterDate).getTime();
    case "<":
      return rowTime < startOfDay(filterDate).getTime();
    case "<=":
      return rowTime <= endOfDay(filterDate).getTime();
    default:
      return true;
  }
}

/**
 * Оценка дерева фильтров только по dat (остальные поля считаем уже учтёнными API).
 * Поддерживает and/or и бинарный формат DevExtreme [left, "and"|"or", right, ...].
 */
function rowMatchesDateFilterTree(rawFilter, rowDat) {
  if (!rawFilter || !Array.isArray(rawFilter)) return true;

  const evalNode = (node) => {
    if (!Array.isArray(node)) return true;

    if (node.length === 3 && FILTER_OPERATORS.includes(node[1])) {
      if (getApiField(node[0]) !== "dat") return true;
      return rowMatchesDateCondition(rowDat, node[1], node[2]);
    }

    if (node.length >= 2 && (node[0] === "and" || node[0] === "or")) {
      const parts = node.slice(1);
      return node[0] === "and"
        ? parts.every(evalNode)
        : parts.some(evalNode);
    }

    // [expr, "and"|"or", expr, "and"|"or", expr, ...]
    if (
      node.length >= 3 &&
      (node[1] === "and" || node[1] === "or")
    ) {
      let result = evalNode(node[0]);
      for (let i = 1; i < node.length - 1; i += 2) {
        const op = node[i];
        const next = node[i + 1];
        if (op === "and") result = result && evalNode(next);
        else if (op === "or") result = result || evalNode(next);
        else return node.every(evalNode);
      }
      return result;
    }

    return node.every(evalNode);
  };

  return evalNode(rawFilter);
}

function applyDateColumnFilterTree(rows, rawFilter) {
  if (!hasDateColumnFilter(rawFilter)) return rows;
  return rows.filter((row) => rowMatchesDateFilterTree(rawFilter, row.dat));
}

function parseAuditStatSort(sortOptions) {
  let sortBy = "dat";
  let sortOrder = "DESC";

  if (sortOptions?.length) {
    const sortRule = sortOptions[0];
    const selector =
      typeof sortRule === "string" ? sortRule : sortRule?.selector;
    if (typeof selector === "string") {
      sortBy = getApiField(selector);
      sortOrder = sortRule?.desc ? "DESC" : "ASC";
    }
  }

  return { sortBy, sortOrder };
}

function mapAuditStatRows(apiList, skip) {
  return (apiList ?? []).map((item, index) => {
    const email = item.user_name ?? item.email ?? "";
    const dogovor = item.ndog ?? item.dogovor ?? "";
    const entry_count =
      item.duration !== null && item.duration !== undefined
        ? item.duration
        : item.entry_count ?? 0;

    const dat = toDate(item.dat) ?? item.dat;

    return {
      ...item,
      id:
        item.id ??
        `${skip + index}-${item.dat ?? ""}-${email}-${dogovor}`,
      dat,
      email,
      dogovor,
      entry_count,
    };
  });
}

/** Тянем все страницы за период — нужно для корректной пагинации после клиентского фильтра по dat. */
async function fetchAllAuditStatPages(periodStart, periodEnd, query) {
  const all = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const res = await getAuditStat("", periodStart, periodEnd, {
      ...query,
      limit: DATE_FETCH_PAGE_SIZE,
      offset,
    });

    if (!res.isOk) {
      return { isOk: false, data: [], total: 0 };
    }

    const chunk = mapAuditStatRows(res.data, offset);
    all.push(...chunk);
    total = res.pagination?.total ?? all.length;

    if (chunk.length === 0) break;
    offset += DATE_FETCH_PAGE_SIZE;
  }

  return { isOk: true, data: all, total: all.length };
}

const AuditPage = () => {
  const { selectedDog } = useAuth();
  const [visiblePage, setVisiblePage] = useState(true);
  const [loading, setLoading] = useState(false);
  /*const [columnHidingEnabled, setColumnHidingEnabled] = useState(
    window.innerWidth < 1024
  );*/

  const msInDay = 1000 * 60 * 60 * 24;
  const now = new Date();
  const defaultStart = new Date(now.getTime() - msInDay * 3);
  const defaultEnd = new Date(now.getTime() + msInDay * 3);
  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const dataGridRef = useRef(null);
  const periodRowsCacheRef = useRef({ key: "", rows: [] });

  const auditStore = useMemo(() => {
    return new CustomStore({
      key: "id",
      load: async (loadOptions) => {
        try {
          setLoading(true);

          const skip = loadOptions.skip ?? 0;
          const take = loadOptions.take ?? 50;
          const { sortBy, sortOrder } = parseAuditStatSort(loadOptions.sort);
          const combinedFilter = loadOptions.filter ?? null;
          const filters = parseAuditStatFilters(combinedFilter);
          const hasDatFilter = hasDateColumnFilter(combinedFilter);

          const queryBase = {
            sortBy,
            sortOrder,
            email: filters.email ?? "",
            userId: filters.user_id ?? "",
            fio: filters.fio ?? "",
            entryCount: filters.entry_count ?? "",
            dogovor: filters.dogovor ?? "",
          };

          // Фильтр колонки «Дата входа» — только на фронте, поверх глобального периода.
          // Чтобы пагинация/total были верными, сначала берём все строки периода (+ API-фильтры),
          // потом режем по dat и уже затем skip/take.
          if (hasDatFilter) {
            const cacheKey = buildPeriodCacheKey(
              periodStart,
              periodEnd,
              queryBase
            );
            let periodRows = periodRowsCacheRef.current.rows;

            if (periodRowsCacheRef.current.key !== cacheKey) {
              const allRes = await fetchAllAuditStatPages(
                periodStart,
                periodEnd,
                queryBase
              );

              if (!allRes.isOk) {
                setLoading(false);
                return { data: [], totalCount: 0 };
              }

              periodRows = allRes.data;
              periodRowsCacheRef.current = { key: cacheKey, rows: periodRows };
            }

            const filtered = applyDateColumnFilterTree(
              periodRows,
              combinedFilter
            );
            const page = filtered.slice(skip, skip + take);
            setLoading(false);
            return { data: page, totalCount: filtered.length };
          }

          const resAudit = await getAuditStat("", periodStart, periodEnd, {
            ...queryBase,
            limit: take,
            offset: skip,
          });

          if (resAudit.isOk) {
            const mappedData = mapAuditStatRows(resAudit.data, skip);
            const total = resAudit.pagination?.total ?? mappedData.length;
            setLoading(false);
            return { data: mappedData, totalCount: total };
          }

          setLoading(false);
          return { data: [], totalCount: 0 };
        } catch (error) {
          console.error("Error loading audit data:", error);
          setLoading(false);
          return { data: [], totalCount: 0 };
        }
      },
    });
  }, [periodStart, periodEnd]);

  useEffect(() => {
    setVisiblePage(true);
    periodRowsCacheRef.current = { key: "", rows: [] };
  }, [selectedDog, periodStart, periodEnd]);

  // Отслеживаем изменение размера окна для адаптивного columnHidingEnabled
  /*useEffect(() => {
    const handleResize = () => {
      setColumnHidingEnabled(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);*/

  const calendarOptions = {
    // applyValueMode: "useButtons",
    // showDropDownButton:true,
  };

  const onEndPickerChanged = useCallback((e) => {
    if (e?.value) {
      setPeriodEnd(e.value);
    }
  }, []);

  const onStartPickerChanged = useCallback((e) => {
    setPeriodStart(e.value);
  }, []);

  const onExporting = (e) => {
    e.cancel = true; // Отменяем стандартный экспорт

    const workbook = new Workbook();
    const period = `${periodStart.toLocaleDateString("ru-ru", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })} - ${periodEnd.toLocaleDateString("ru-ru", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })}`;

    // Проверяем, есть ли выбранные строки
    const selectedRowKeys = e.component.getSelectedRowKeys();
    const dataSource = e.component.getDataSource();
    const totalCount = dataSource.totalCount() || 0;
    const isSelectedOnly =
      selectedRowKeys &&
      selectedRowKeys.length > 0 &&
      selectedRowKeys.length < totalCount;

    const worksheetName = isSelectedOnly
      ? `Аудит (выбранные) ${period}`
      : `Аудит ${period}`;
    const fileName = isSelectedOnly
      ? `Аудит (выбранные) ${period}.xlsx`
      : `Аудит ${period}.xlsx`;
    const worksheet = workbook.addWorksheet(worksheetName);

    // Настройки экспорта
    const exportOptions = {
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
      keepColumnWidths: false,
      topLeftCell: { row: 1, column: 1 },
      selectedRowsOnly: isSelectedOnly, // Экспортируем только выбранные строки, если они есть
    };

    // Экспортируем данные из DataGrid
    exportDataGrid(exportOptions)
      .then(() => {
        workbook.xlsx
          .writeBuffer()
          .then((buffer) => {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              fileName
            );
          })
          .catch((error) => {
            console.error("Ошибка при создании файла Excel:", error);
          });
      })
      .catch((error) => {
        console.error("Ошибка при экспорте данных:", error);
      });
  };

  return (
    <>
      {visiblePage ? (
        <div className={"content-block dx-card responsive-paddings"}>
          <FontAwesomeIcon
            className="icon-header-h3 main-color main-color-light"
            icon={faListCheck}
          />
          <h3>
            <strong>Статистика посещений</strong>
          </h3>
          <div className="row big-input">
            <div
              className={"col-sm-4 col-md-3 col-lg-2"}
              style={{ minWidth: "200px" }}
            >
              <DateBox
                labelMode={"floating"}
                id={"db1"}
                label={"Период начала:"}
                calendarOptions={calendarOptions}
                useMaskBehavior={true}
                value={periodStart}
                onValueChanged={onStartPickerChanged}
              />
            </div>
            <div
              className={"col-sm-4 col-md-3 col-lg-2 mt20xs"}
              style={{ minWidth: "200px" }}
            >
              <DateBox
                labelMode={"floating"}
                id={"db2"}
                label={"Период окончания:"}
                calendarOptions={calendarOptions}
                useMaskBehavior={true}
                value={periodEnd}
                onValueChanged={onEndPickerChanged}
              />
            </div>
          </div>
          <div style={{ textAlign: "justify" }}>
            <DataGrid
              ref={dataGridRef}
              key={`audit-grid-${periodStart.getTime()}-${periodEnd.getTime()}`}
              height={660}
              style={{ margin: "20px 0px 20px 0px" }}
              keyExpr="id"
              allowColumnReordering={true}
              rowAlternationEnabled={true}
              showBorders={true}
              columnAutoWidth={false}
              noDataText={`За выбранный период записи отсутствуют`}
              dataSource={auditStore}
              onExporting={onExporting}
              loadPanel={{ enabled: false }}
              className={'export-btn-no-wrap'}
              allowColumnResizing={true}
              columnWidth={'auto'}
              width={'100%'}
            >
              <RemoteOperations
                paging={true}
                sorting={true}
                filtering={true}
                grouping={false}
              />
              <Export
                enabled={true}
                allowExportSelectedData={true}
                texts={{
                  exportAll: "Экспортировать все строки в Excel",
                  exportSelected: "Экспортировать выбранные строки в Excel",
                }}
              />
              <Selection mode="multiple" />
              <Scrolling mode="standard" />
              <GroupPanel visible={true} />
              <Grouping autoExpandAll={true} />
              <Sorting mode="single" />
              <FilterRow visible={true} />
              <HeaderFilter visible={true} />
              <SearchPanel visible={true} width={240} placeholder="Поиск..." />
              <Paging defaultPageSize={50} enabled={true} />
              <Pager
                visible={true}
                allowedPageSizes={[5, 10, 20, 50, 100]}
                displayMode={"full"}
                showPageSizeSelector={true}
                showInfo={true}
                showNavigationButtons={true}
                infoText="Страница {0} из {1} ({2} записей)"
              />
              <Column
                type="selection"
                width="3%"
                minWidth={50}
                visible={true}
              />
              <Column
                dataField="dat"
                width="11%"
                minWidth={140}
                caption="Дата входа"
                dataType="datetime"
                format="dd.MM.yyyy HH:mm"
                allowEditing={false}
                allowHeaderFiltering={false}
                filterOperations={DATE_COLUMN_FILTER_OPERATIONS}
                selectedFilterOperation="="
                filterValue={null}
                calculateFilterExpression={calculateDatFilterExpression}
                sortOrder="desc"
                defaultSortIndex={0}
                visible={true}
              />
              <Column
                dataField="user_id"
                width="4%"
                minWidth={60}
                caption="ID Пользователя"
                dataType="number"
                allowEditing={false}
                visible={true}
              />
              <Column
                dataField="fio"
                width="28%"
                minWidth={200}
                caption="ФИО"
                dataType="string"
                allowEditing={false}
                visible={true}
              />
              <Column
                dataField="email"
                width="28%"
                minWidth={200}
                caption="E-mail"
                dataType="string"
                allowEditing={false}
                visible={true}
              />
              <Column
                dataField="entry_count"
                width="13%"
                minWidth={120}
                caption="Количество вхождений"
                dataType="number"
                allowEditing={false}
                visible={true}
              />
              <Column
                dataField="dogovor"
                width="13%"
                minWidth={120}
                caption="Договор"
                dataType="string"
                allowEditing={false}
                visible={true}
              />
            </DataGrid>
          </div>
          <LoadPanel visible={loading} showIndicator={true} />
        </div>
      ) : (
        <AccessDenied />
      )}
    </>
  );
};

export default AuditPage;
